import { derived, get, writable } from 'svelte/store';
import { assets, playSound, waitForAssets } from './assets';
import { auth, logout } from './auth';
import type {
  ChatMessage,
  EventEntry,
  EventsBatch,
  LobbyPlayer,
  Map,
  Player,
  PublicPlayer,
} from './backend';
import { enumIs } from './enums';
import { ClientGame, ClientOwnPlayer, EnemyPlayer, Renderer } from './game';
import { renderMap, TILE_SIZE } from './map';
import { unreachable } from './unreachable';

export type InGameState = {
  state: 'in-game';
  map: Map;
  // renderedMap: HTMLCanvasElement;
  lastProcessedRound: number | null;
  round: number;
  players: PublicPlayer[];
  player: Player;
  game: ClientGame;
  renderer: Renderer;
};

export type InLobbyState = {
  state: 'lobby';
  players: LobbyPlayer[];
  gameInProgress: boolean;
  chat: ChatMessage[];
};

export type GameState =
  | {
      state: 'uninitialized';
    }
  | {
      state: 'outside';
    }
  | InLobbyState
  | InGameState;

export const gameState = writable<GameState>({ state: 'uninitialized' });

export const events = writable<{ rounds: number; events: EventEntry[] } | null>(
  null
);

// let cleanup: () => void;
auth.subscribe((authState) => {
  if (authState.state === 'authenticated') {
    gameStateLoop();
  } else {
    gameState.update(() => ({ state: 'uninitialized' }));
    // if (cleanup) {
    //   cleanup();
    // }
  }
});

function consolidateEvents(batches: EventsBatch[]): EventEntry[] {
  const allEvents: EventEntry[] = [];
  // let t0 = 0;
  for (const batch of batches) {
    for (const event of batch.events) {
      allEvents.push({
        ...event,
        start_time: event.start_time,
        end_time: event.end_time,
      });
    }

    // t0 += 3.0;
  }
  return allEvents;
}

async function gameStateLoop() {
  console.log('Starting game state loop');

  while (true) {
    const currentAuth = get(auth);
    if (currentAuth.state !== 'authenticated') {
      break;
    }
    try {
      const playerState = await currentAuth.actor.get_player_state(
        BigInt(Date.now())
      );

      // switch (currentGameState.state) {
      // case 'uninitialized':
      if (enumIs(playerState, 'Outside')) {
        gameState.update(() => ({ state: 'outside' }));
      } else if (enumIs(playerState, 'InLobby')) {
        const updatedPlayers = playerState.InLobby.players;

        gameState.update((currentState) => {
          if (
            currentState.state === 'lobby' &&
            updatedPlayers.some(
              (p) =>
                !currentState.players.find(
                  (cp) => cp.principal.compareTo(p.principal) === 'eq'
                )
            )
          ) {
            // new player joined
            playSound(assets.sounds.reload);
          }

          return {
            state: 'lobby',
            players: playerState.InLobby.players,
            gameInProgress: playerState.InLobby.game_in_progress,
            chat: playerState.InLobby.chat,
          };
        });
      } else if (enumIs(playerState, 'InGame')) {
        await waitForAssets();

        gameState.update(() => ({
          state: 'in-game',
          lastProcessedRound: null,
          map: playerState.InGame.map,
          round: playerState.InGame.round,
          players: playerState.InGame.players,
          player: playerState.InGame.player,
          // renderedMap: renderMap(playerState.InGame.map),

          game: new ClientGame(
            playerState.InGame.players,

            new ClientOwnPlayer(
              playerState.InGame.player.principal.toText(),
              { ...playerState.InGame.player.loc, angle: 0 },
              playerState.InGame.player.health,
              playerState.InGame.player.max_health
            ),
            playerState.InGame.players
              .filter(
                (p) =>
                  p.principal.toText() !==
                  playerState.InGame.player.principal.toText()
              )
              .map(
                (p) =>
                  new EnemyPlayer(
                    p.name,
                    p.principal.toText(),
                    null,
                    null,
                    null
                  )
              ),
            Number(playerState.InGame.started_at_real_time / 1000000n),
            playerState.InGame.game_type
          ),
          renderer: new Renderer(playerState.InGame.map, TILE_SIZE),
        }));

        const allEvents = consolidateEvents(playerState.InGame.events);

        events.set({
          rounds: playerState.InGame.events.length,
          events: allEvents,
        });
      } else {
        unreachable(playerState);
      }
      // }

      if (enumIs(playerState, 'InGame')) {
        let lastRound = playerState.InGame.round - 1;
        // let lastRoundTime = Date.now();
        // let lastBlockT0 = Date.now();
        while (true) {
          let maybeNewEvents = await currentAuth.actor.get_events(
            [Math.max(0, lastRound)],
            BigInt(Date.now())
          );

          if (enumIs(maybeNewEvents, 'Events')) {
            if (maybeNewEvents.Events.length === 0) {
              // console.log('No new events');
            } else {
              // console.log(`New round in ${Date.now() - lastRoundTime}ms`);

              // console.log(
              //   `[${Date.now() - lastBlockT0}] Got events from round ${
              //     maybeNewEvents.Events[0].round
              //   } to ${
              //     maybeNewEvents.Events[maybeNewEvents.Events.length - 1].round
              //   }`
              // );
              lastRound =
                maybeNewEvents.Events[maybeNewEvents.Events.length - 1].round;
              // lastBlockT0 = Date.now();

              const allEvents = consolidateEvents(maybeNewEvents.Events);

              if (allEvents.length > 0) {
                events.set({
                  rounds: maybeNewEvents.Events.length,
                  events: allEvents,
                });
              }

              // lastRoundTime = Date.now();
            }
          } else if (enumIs(maybeNewEvents, 'NotInGame')) {
            console.log('Not in game!');

            break;
          } else if (enumIs(maybeNewEvents, 'EventError')) {
            console.log('Event error!', maybeNewEvents.EventError);
          } else {
            unreachable(maybeNewEvents);
          }

          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      } else {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    } catch (e) {
      console.log(e);

      logout();
    }
  }
}
